import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  Center,
  SimpleGrid,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FiClock, FiInbox, FiRefreshCcw } from "react-icons/fi";
import { fetchDashboardSummary } from "../redux/actions/dashboardActions";
import ReactApexChart from "react-apexcharts";
import { formatAUDate, formatAUTime } from "../global-client";

const formatCurrency = (value) =>
  new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD", maximumFractionDigits: 0 }).format(value || 0);

const formatNumber = (value) => new Intl.NumberFormat("en-US").format(value || 0);

const StatCard = ({ label, value, hint, accent, actionLabel, onAction, actionLoading }) => {
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.100", "gray.700");
  const labelColor = useColorModeValue("gray.600", "gray.300");
  const hintColor = useColorModeValue("gray.600", "gray.400");

  return (
    <Box
      bg={cardBg}
      borderRadius='xl'
      boxShadow='lg'
      py='3'
      px='5'
      borderWidth='1px'
      borderColor={cardBorder}
      position='relative'
      overflow='hidden'
    >
      <Box
        position='absolute'
        inset='0'
        bgGradient={`linear(135deg, ${accent}26, transparent 60%)`}
        pointerEvents='none'
      />
      {actionLabel && onAction && (
        <Button
          size='xs'
          variant='outline'
          position='absolute'
          top='2'
          right='2'
          zIndex='1'
          onClick={(e) => {
            e.stopPropagation()
            onAction()
          }}
          isLoading={actionLoading}
        >
          {actionLabel}
        </Button>
      )}
      <Stat>
        <StatLabel fontWeight='medium' color={labelColor}>
          {label}
        </StatLabel>
        <StatNumber fontSize='2xl' fontWeight='black'>
          {value}
        </StatNumber>
        {hint && (
          <StatHelpText color={hintColor} fontWeight='medium'>
            {hint}
          </StatHelpText>
        )}
      </Stat>
    </Box>
  );
};

const MoneyStack = ({ totals }) => {
  const entryBg = useColorModeValue("gray.50", "gray.700");
  const entries = [
    { label: "Cash", value: totals.cash, color: "teal" },
    { label: "Cards", value: totals.cards, color: "cyan" },
    { label: "Bank", value: totals.bank, color: "blue" },
    { label: "Cheque", value: totals.cheque, color: "orange" },
    { label: "MOTO", value: totals.moto, color: "pink" },
  ].filter((entry) => entry.value);

  if (entries.length === 0) return null;

  return (
    <VStack align='stretch' spacing='3'>
      {entries.map((entry) => (
        <Flex key={entry.label} justify='space-between' align='center' bg={entryBg} p='3' rounded='lg'>
          <Text fontWeight='semibold'>{entry.label}</Text>
          <Badge colorScheme={entry.color} variant='subtle' fontSize='md'>
            {formatCurrency(entry.value)}
          </Badge>
        </Flex>
      ))}
    </VStack>
  );
};

const DashboardScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast()
  const { summary, loading, error, lastUpdated } = useSelector((state) => state.dashboard);
  // Keep previous summary to avoid flicker
  const prevSummaryRef = React.useRef();
  useEffect(() => {
    if (summary) prevSummaryRef.current = summary;
  }, [summary]);
  const displaySummary = summary || prevSummaryRef.current || {};
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const headerGradient = useColorModeValue("linear(to-r, teal.400, cyan.400)", "linear(to-r, gray.800, teal.700)");
  const timestampColor = useColorModeValue("white", "gray.100");
  const panelBg = useColorModeValue("white", "gray.800");
  const panelBorder = useColorModeValue("gray.100", "gray.700");
  const messageBodyColor = useColorModeValue("gray.600", "gray.300");
  const messageEmptyColor = useColorModeValue("gray.500", "gray.400");
  const chartTrackBg = useColorModeValue("#edf2f7", "#2d3748");
  const chartThemeMode = useColorModeValue("light", "dark");

  useEffect(() => {
    dispatch(fetchDashboardSummary());
    const interval = setInterval(() => {
      dispatch(fetchDashboardSummary());
    }, 30000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const totals = displaySummary.totals || {};
  const metrics = displaySummary.metrics || {};
  const scheduled = displaySummary.scheduled || {};

  const fetchedAt1 = metrics.googleReviewsFetchedAt ? formatAUTime(metrics.googleReviewsFetchedAt) : null
  const fetchedAt2 = metrics.googleReviews2FetchedAt ? formatAUTime(metrics.googleReviews2FetchedAt) : null

  const cards = [
    {
      label: "Jobs Due Today",
      value: formatNumber(metrics.jobsToday),
      hint: "Scheduled for today (or past)",
      accent: "#805ad5",
      onClick: () => {
        localStorage.setItem("jobsListType", "1");
        navigate("/jobs");
      },
    },
    {
      label: "All Current Jobs",
      value: formatNumber(metrics.outstandingJobs),
      hint: "Including today and future",
      accent: "#0fb9b1",
      onClick: () => {
        localStorage.setItem("jobsListType", "1");
        navigate("/jobs");
      },
    },
    {
      label: "Completed today",
      value: formatNumber(metrics.completedToday),
      hint: "Jobs marked complete today",
      accent: "#38b2ac",
      onClick: () => {
        localStorage.setItem("jobsListType", "2");
        navigate("/jobs");
      },
    },
    {
      label: "Bookings For Today",
      value: formatNumber(metrics.bookings),
      hint: "Booking for today not yet turned into jobs",
      accent: "#2b6cb0",
      onClick: () => {
        localStorage.setItem("jobsListType", "4");
        navigate("/jobs");
      },
    },
    {
      label: "Total Bookings",
      value: formatNumber(metrics.allBookings),
      hint: "Bookings for today and future",
      accent: "#f6ad55",
      onClick: () => {
        localStorage.setItem("jobsListType", "4");
        navigate("/jobs");
      },
    },
    {
      label: "Bookings received Today",
      value: formatNumber(metrics.bookingRequests),
      hint: "Booking requests received today",
      accent: "#2b6cb0",
      onClick: () => {
        localStorage.setItem("jobsListType", "4");
        navigate("/jobs");
      },
    },
    {
      label: "Google Reviews",
      value: formatNumber(metrics.googleReviews || 0),
      hint: fetchedAt1 ? `Total reviews for Game Freaks • Updated ${fetchedAt1}` : "Total reviews for Game Freaks",
      accent: "#e53e3e",
      actionLabel: "Fetch Now",
      onAction: async () => {
        toast({
          title: "Refreshing Google reviews",
          description: "For both locations",
          status: "info",
          duration: 1500,
          isClosable: true,
        })
        const data = await dispatch(fetchDashboardSummary(null, null, true))
        const t1 = data?.metrics?.googleReviewsFetchedAt ? formatAUTime(data.metrics.googleReviewsFetchedAt) : null
        const t2 = data?.metrics?.googleReviews2FetchedAt ? formatAUTime(data.metrics.googleReviews2FetchedAt) : null
        toast({
          title: "Google reviews updated",
          description: `GF: ${formatNumber(data?.metrics?.googleReviews || 0)}${t1 ? ` at ${t1}` : ""} • PS5: ${formatNumber(data?.metrics?.googleReviews2 || 0)}${t2 ? ` at ${t2}` : ""}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        })
      },
      actionLoading: loading,
      onClick: () => {
        window.open(
          "https://www.google.com/search?q=Game+Freaks+Melbourne&stick=H4sIAAAAAAAA_-NgU1I1qDBLTDEzSTM3Tkw0NzdIMUyxMqhINLA0sjAxMDMzSLM0sUwyWcQq6p6Ym6rgVpSamF2s4Juak5RfWpSXCgBrC4UKQQAAAA&hl=en-GB&mat=CXXx_xVLNbyWElcBTVDHntIXwjh3AqPKEOx5eMOF0mQ4cGTg19GqJ4ZfGt9OxoF6b0CcxiBijTseboIOtxNNYYmJHS2a8ZDeEYBgQ15YYHTr7nq5XS_dDkCgYdy4_3dpDT4&authuser=0&ved=2ahUKEwi624HF4tWQAxUpla8BHbG1H7AQ-MgIegQIIRAe#",
          "_blank"
        );
      },
    },
    {
      label: "Google Reviews 2",
      value: formatNumber(metrics.googleReviews2 || 0),
      hint: fetchedAt2 ? `Total reviews for PS5 Repairs Melbourne • Updated ${fetchedAt2}` : "Total reviews for PS5 Repairs Melbourne",
      accent: "#dd6b20",
      actionLabel: "Fetch Now",
      onAction: async () => {
        toast({
          title: "Refreshing Google reviews",
          description: "For both locations",
          status: "info",
          duration: 1500,
          isClosable: true,
        })
        const data = await dispatch(fetchDashboardSummary(null, null, true))
        const t1 = data?.metrics?.googleReviewsFetchedAt ? formatAUTime(data.metrics.googleReviewsFetchedAt) : null
        const t2 = data?.metrics?.googleReviews2FetchedAt ? formatAUTime(data.metrics.googleReviews2FetchedAt) : null
        toast({
          title: "Google reviews updated",
          description: `GF: ${formatNumber(data?.metrics?.googleReviews || 0)}${t1 ? ` at ${t1}` : ""} • PS5: ${formatNumber(data?.metrics?.googleReviews2 || 0)}${t2 ? ` at ${t2}` : ""}`,
          status: "success",
          duration: 3000,
          isClosable: true,
        })
      },
      actionLoading: loading,
      onClick: () => {
        window.open(
          "https://www.google.com/search?sca_esv=b315ce123a2264f5&sxsrf=AE3TifMA4RmDwSJ6ZOy5Je6_6biZ5bThbg%3A1765920849144&q=PS5%20Repairs%20Melbourne&stick=H4sIAAAAAAAAAONgU1I1qDBLTDEzSTM3NjCwMDBNM7S0MqiwTDY2SDQzNrZMSzU3TzE1WsQqGhBsqhCUWpCYWVSs4Juak5RfWpSXCgBmFiZDQQAAAA&mat=Cdj1GY24UQ06&ved=2ahUKEwiOuajnh8ORAxUcbmwGHQL0EIAQrMcEegQIGhAC#",
          "_blank"
        );
      },
    },
  ];

  // Persist previous payment data to avoid flicker
  const prevPayment = React.useRef({ series: [0, 0, 0, 0, 0], totals: {}, hasData: false });
  const paymentSeries = [totals.cash || 0, totals.cards || 0, totals.bank || 0, totals.cheque || 0, totals.moto || 0];
  const paymentLabels = ["Cash", "Cards", "Bank", "Cheque", "MOTO"];
  const paymentHasData = paymentSeries.some((val) => Number(val) > 0);
  if (paymentHasData) {
    prevPayment.current = { series: paymentSeries, totals, hasData: true };
  }
  const displayPaymentSeries = paymentHasData ? paymentSeries : prevPayment.current.series;
  const displayPaymentHasData = paymentHasData || prevPayment.current.hasData;

  // Persist previous workload data to avoid flicker
  const prevWorkload = React.useRef({ series: [0, 0, 0], hasData: false });
  const workloadSeries = [metrics.outstandingJobs || 0, metrics.bookings || 0, metrics.jobsToday || 0];
  const workloadLabels = ["Outstanding", "Bookings", "Today"];
  const workloadHasData = workloadSeries.some((val) => Number(val) > 0);
  if (workloadHasData) {
    prevWorkload.current = { series: workloadSeries, hasData: true };
  }
  const displayWorkloadSeries = workloadHasData ? workloadSeries : prevWorkload.current.series;
  const displayWorkloadHasData = workloadHasData || prevWorkload.current.hasData;

  return (
    <Box bg={pageBg} minH='100vh' pb='8'>
      <Box
        px={{ base: 4, md: 8, xl: 10 }}
        py='6'
        bgGradient={headerGradient}
        color='white'
        borderBottomRadius='2xl'
        boxShadow='xl'
      >
        <Flex
          justify='space-between'
          align={{ base: "flex-start", md: "center" }}
          direction={{ base: "column", md: "row" }}
          gap='4'
        >
          <Box>
            <HStack spacing='2' justify='center' align='center' mb='1'>
              <Text fontSize='xs' opacity={0.85} mr='4'>
                Operations Dashboard
              </Text>
              <HStack spacing='2' color={timestampColor} fontSize='sm'>
                <Icon as={FiClock} />
                <Text fontWeight='medium'>
                  {lastUpdated ? `Updated ${formatAUTime(lastUpdated)}` : "Loading live data..."}
                </Text>
              </HStack>
            </HStack>
            <Text fontSize={{ base: "2xl", md: "3xl" }} fontWeight='black'>
              Today’s snapshot
            </Text>
          </Box>
          <HStack spacing='2' mt={{ base: 0, md: 0 }}>
            <Button
              leftIcon={<FiRefreshCcw />}
              variant='outline'
              color='white'
              borderColor='whiteAlpha.700'
              _hover={{ bg: "whiteAlpha.200" }}
              onClick={() => dispatch(fetchDashboardSummary())}
              isLoading={loading}
            >
              Refresh
            </Button>
            <Badge colorScheme='blackAlpha' variant='solid' px='2.5' py='1.5' fontSize='xs' borderRadius='md'>
              Daily view
            </Badge>
          </HStack>
        </Flex>
      </Box>

      <Box px={{ base: 4, md: 8, xl: 10 }} mt={{ base: -4, md: -6 }}>
        {error && (
          <Alert status='error' borderRadius='lg' mb='4'>
            <AlertIcon />
            <Box>
              <AlertTitle>Dashboard data unavailable</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
          </Alert>
        )}

        <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={{ base: 3, md: 4 }}>
          {cards.map((card) => (
            <Box
              key={card.label}
              borderRadius='xl'
              cursor={card.onClick ? "pointer" : "default"}
              _hover={card.onClick ? { boxShadow: "xl", transform: "scale(1.03)" } : {}}
              transition='all 0.15s'
              onClick={(e) => {
                // Ignore clicks on inner action buttons
                const t = e.target
                if (t && (t.closest("button") || t.closest("[role=button]"))) return
                if (card.onClick) card.onClick()
              }}
            >
              <StatCard
                label={card.label}
                value={card.value}
                hint={card.hint}
                accent={card.accent}
                actionLabel={card.actionLabel}
                onAction={card.onAction}
                actionLoading={card.actionLoading}
              />
            </Box>
          ))}
        </SimpleGrid>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={{ base: 4, md: 5 }} mt='4'>
          <Box
            bg={panelBg}
            p='5'
            rounded='2xl'
            boxShadow='lg'
            borderWidth='1px'
            borderColor={panelBorder}
            cursor={"pointer"}
            _hover={{ boxShadow: "xl", transform: "scale(1.03)" }}
            onClick={() => {
              navigate("/totals");
            }}
          >
            <Flex justify='space-between' align='center' mb='4'>
              <HStack spacing='3'>
                <Icon as={FiInbox} />
                <Text fontWeight='bold' fontSize='lg'>
                  Revenue today
                </Text>
              </HStack>
              <Badge colorScheme='teal' variant='subtle' px='3' py='1'>
                {formatCurrency(totals.total)}
              </Badge>
            </Flex>
            <Divider mb='4' />
            <MoneyStack totals={totals} />
          </Box>

          <Box
            bg={panelBg}
            p='5'
            rounded='2xl'
            boxShadow='lg'
            borderWidth='1px'
            borderColor={panelBorder}
            position='relative'
            overflow='hidden'
            cursor={"pointer"}
            _hover={{ boxShadow: "xl", transform: "scale(1.03)" }}
            onClick={() => {
              localStorage.setItem("messagesTab", "1");
              navigate("/messages");
            }}
          >
            <Box
              position='absolute'
              inset='0'
              bgGradient='linear(120deg, #4fd1c5 0%, transparent 70%)'
              opacity={0.08}
            />
            <Flex justify='space-between' align='center' mb='4'>
              <Text fontWeight='bold' fontSize='lg'>
                Messaging queue
              </Text>
              <Badge colorScheme='pink' variant='subtle' px='3' py='1'>
                {formatNumber(scheduled.count)} queued
              </Badge>
            </Flex>
            <Text color={messageBodyColor} mb='3'>
              {scheduled.nextScheduledAt
                ? `Next send on ${formatAUDate(scheduled.nextScheduledAt)} at ${formatAUTime(
                    scheduled.nextScheduledAt
                  )} `
                : "No future messages waiting"}
            </Text>
            <Button
              variant='outline'
              onClick={() => dispatch(fetchDashboardSummary())}
              leftIcon={<FiRefreshCcw />}
              isLoading={loading}
              size='sm'
            >
              Refresh queue
            </Button>
          </Box>

          <Box bg={panelBg} p='6' rounded='2xl' boxShadow='lg' borderWidth='1px' borderColor={panelBorder}>
            <Flex justify='space-between' align='center' mb='4'>
              <Text fontWeight='bold' fontSize='lg'>
                Payment mix
              </Text>
              <Badge colorScheme='cyan' variant='subtle' px='3' py='1'>
                Today
              </Badge>
            </Flex>
            <Box borderRadius='lg'>
              {displayPaymentHasData ? (
                <ReactApexChart
                  type='donut'
                  height={220}
                  series={displayPaymentSeries}
                  options={{
                    labels: paymentLabels,
                    legend: { position: "bottom" },
                    dataLabels: { enabled: true },
                    theme: { mode: chartThemeMode },
                    colors: ["#0fb9b1", "#319795", "#2b6cb0", "#ed8936", "#ed64a6"],
                    stroke: { width: 2 },
                  }}
                />
              ) : (
                <Center py='12'>
                  <Text color={messageEmptyColor}>No payments recorded for today</Text>
                </Center>
              )}
            </Box>
          </Box>

          <Box bg={panelBg} p='6' rounded='2xl' boxShadow='lg' borderWidth='1px' borderColor={panelBorder}>
            <Flex justify='space-between' align='center' mb='4'>
              <Text fontWeight='bold' fontSize='lg'>
                Workload snapshot
              </Text>
              <Badge colorScheme='purple' variant='subtle' px='3' py='1'>
                Live
              </Badge>
            </Flex>
            <Box borderRadius='lg'>
              {displayWorkloadHasData ? (
                <ReactApexChart
                  type='radialBar'
                  height={220}
                  series={displayWorkloadSeries}
                  options={{
                    labels: workloadLabels,
                    plotOptions: {
                      radialBar: {
                        hollow: { size: "32%" },
                        track: { background: chartTrackBg },
                        dataLabels: { name: { fontSize: "14px" }, value: { fontSize: "16px" } },
                      },
                    },
                    colors: ["#0fb9b1", "#f6ad55", "#805ad5"],
                    legend: { show: true, position: "bottom" },
                    theme: { mode: chartThemeMode },
                  }}
                />
              ) : (
                <Center py='12'>
                  <Text color={messageEmptyColor}>No workload data yet</Text>
                </Center>
              )}
            </Box>
          </Box>
        </SimpleGrid>
      </Box>
    </Box>
  );
};

export default DashboardScreen;
